import { useCallback, useEffect, useRef, useState } from 'react'

type UseDialogState = {
    /*
     * defaultOpen: boolean that indicates if the dialog should be open by default
     */
    defaultOpen?: boolean
    /*
     * shouldCloseIf: boolean that indicates if the dialog should close
     */
    shouldCloseIf?: boolean
    /*
     * onClose: Array of functions to be called when the dialog is closed. The onClose function should be stable, please wrap it in useCallback if needed
     */
    onClose?: () => void
}

export const useDialogState = ({ onClose, defaultOpen, shouldCloseIf }: UseDialogState) => {
    const [isOpen, setIsOpen] = useState(false)
    const isMounted = useRef(false)
    const onCloseRef = useRef(onClose)

    useEffect(() => {
        onCloseRef.current = onClose
    }, [onClose])

    useEffect(() => {
        if (defaultOpen) {
            // eslint-disable-next-line react/set-state-in-effect -- syncs this state after commit, as it always has; setting it during render would change when the sync runs
            setIsOpen(defaultOpen)
        }
    }, [defaultOpen])

    useEffect(() => {
        isMounted.current = true
        return () => {
            isMounted.current = false
        }
    }, [])

    const onOpenChange = useCallback((isOpen: boolean) => {
        if (!isMounted.current) return

        setIsOpen(isOpen)

        if (!isOpen && onCloseRef.current) {
            onCloseRef.current()
        }
    }, [])

    useEffect(() => {
        if (shouldCloseIf) {
            onOpenChange(false)
        }
    }, [shouldCloseIf, onOpenChange])

    return { isOpen, onOpenChange }
}
